// Room texture render helpers extracted 1:1 from RoomCanvas.tsx.
// Operate directly on PIXI sprites/textures (mutate them) — no React state.
// `loadedTexturePathsRef.current` (a Set<string> tracked in RoomCanvas) is injected as a
// parameter so these stay pure functions; callers pass the ref's `.current` Set, whose
// mutations (.add()/.delete()) are visible back in RoomCanvas since Sets are by-reference.
import * as PIXI from 'pixi.js';
import { smoothWhenMinified } from './textureFilter';

/**
 * Pixels of room art (furniture, walls, floors) per world unit. Drawn at 4x
 * (art/room-originals/), shipped at 2x by tools/atlas/build-room-art.mjs — a
 * quarter of the GPU memory, pixel-for-pixel up to 1x zoom on a 2x screen.
 * In-game size = picture size / ROOM_ART_SCALE.
 */
export const ROOM_ART_SCALE = 2;

export const applyRoomTextureScale = (sprite: PIXI.Sprite, texture: PIXI.Texture) => {
  sprite.width = texture.width / ROOM_ART_SCALE;
  sprite.height = texture.height / ROOM_ART_SCALE;
  // Walls and floors are mostly seen zoomed out: mipmaps keep them from boiling.
  smoothWhenMinified(texture.source);
};

export const applyWallSpriteTexture = (
  sprite: PIXI.Sprite,
  texture: PIXI.Texture,
  mirrored: boolean
) => {
  sprite.texture = texture;
  applyRoomTextureScale(sprite, texture);
  sprite.scale.x = mirrored ? -Math.abs(sprite.scale.x) : Math.abs(sprite.scale.x);
};

// Synchronously free a no-longer-displayed texture from the GPU + caches.
// Mirrors the unmount cleanup logic; used when swapping the wall/floor backdrop so the
// replaced (often very large) texture does not linger in GPU memory. Skips paths that are
// still referenced by furniture sprites or the surviving wall/floor (passed in keepPaths).
export const freeRoomTexture = (
  texPath: string | null,
  keepPaths: Set<string>,
  loadedTexturePaths: Set<string>
) => {
  if (!texPath || keepPaths.has(texPath)) return;
  try {
    const texture = PIXI.Assets.get(texPath) as PIXI.Texture | undefined;
    if (texture?.source) {
      texture.source.unload();
      const res = texture.source.resource as { close?: () => void } | null | undefined;
      if (res && typeof res.close === 'function') res.close();
    }
  } catch { /* texture may already be gone */ }
  try { PIXI.Cache.remove(texPath); } catch { /* ignore */ }
  try {
    const absUrl = PIXI.path.toAbsolute(texPath);
    const loader = PIXI.Assets.loader;
    if (absUrl && loader.promiseCache[absUrl]) delete loader.promiseCache[absUrl];
  } catch { /* ignore */ }
  loadedTexturePaths.delete(texPath);
};
